// Which color a trip is drawn in on the calendar.
//
// Bound by the trip's index in the FULL group list -- the order the picker
// renders -- rather than by its index among the selected trips or by a hash of
// its key. Two consequences, both wanted:
//
//   * a trip keeps its color while the user checks and unchecks others, because
//     the full list doesn't change when the selection does; and
//   * two trips adjacent in the picker always differ, and picker-adjacent means
//     time-adjacent means likely to be touching on the calendar -- exactly the
//     pair that has to be told apart. A key hash guarantees nothing there.
//
// The cost is that adding a trip re-colors the older ones. That's fine: the
// legend is always on screen and the color is a grouping cue, not an identity.
//
// Separate from the map's LEG_COLOR_TOKENS on purpose (see the comment beside
// --tp-series-* in tokens.css): a bar carries text on the color and a route
// line does not, so these are picked for contrast against the label.
//
// "Two trips adjacent in the picker always differ" is a claim about the ORDER of
// the tokens, and it lives in tokens.css -- the values there are written in
// sequence order, chosen so consecutive indices are as far apart as the set
// allows. This list is a straight 1..8 walk of them, so it is not the place to
// resequence: taking them out of order here would leave --tp-series-N meaning one
// thing to CSS and another to the calendar. seriesPalette.test.js measures the
// separation the claim depends on.

// Fallbacks mirror the light values in tokens.css, the same convention
// mapStyles.mjs uses for FALLBACK_ACCENT -- a test that renders without the
// stylesheet still gets a real color rather than an empty custom property. That
// mirroring used to be convention alone; seriesPalette.test.js asserts it now.
export const CALENDAR_COLOR_TOKENS = [
  { cssVar: '--tp-series-1', fallback: '#881ae1' },
  { cssVar: '--tp-series-2', fallback: '#a96000' },
  { cssVar: '--tp-series-3', fallback: '#00359f' },
  { cssVar: '--tp-series-4', fallback: '#d50098' },
  { cssVar: '--tp-series-5', fallback: '#008451' },
  { cssVar: '--tp-series-6', fallback: '#730066' },
  { cssVar: '--tp-series-7', fallback: '#0075b2' },
  { cssVar: '--tp-series-8', fallback: '#772e00' },
];

export function calendarColorToken(index) {
  const n = Number.isFinite(index) ? Math.trunc(index) : 0;
  const wrapped = ((n % CALENDAR_COLOR_TOKENS.length) + CALENDAR_COLOR_TOKENS.length)
    % CALENDAR_COLOR_TOKENS.length;
  return CALENDAR_COLOR_TOKENS[wrapped];
}

// The color as a CSS value, ready to hand to an inline custom property. Unlike
// the map -- where maplibre paint needs a concrete resolved color -- this stays
// a `var()`, so a theme swap repaints the bars with no JS involved at all.
export function calendarColorVar(index) {
  const { cssVar, fallback } = calendarColorToken(index);
  return `var(${cssVar}, ${fallback})`;
}
